import { CacheStrategy } from '@rammetec/data-access';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { ProductionEntryOutputStockStatusValues, PRODUCTION_ENTRY_OUTPUT_STOCK_STATUS_LABELS } from '../types/production-entry.js';
import { PRODUCTION_ENTRY_MATERIAL, } from '../types/production-entry-material.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionEntryMaterial (consumption lines under an entry).
 * Subcollection: org/{orgId}/production-entry/{productionEntryId}/material
 *
 * Resolve repository with context: { orgId, productionEntryId }.
 */
export const productionEntryMaterialDescriptor = {
    name: PRODUCTION_ENTRY_MATERIAL,
    label: 'Consumo do apontamento',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-entry/{productionEntryId}/material',
    pathPatternKeys: {
        orgId: 'string',
        productionEntryId: 'string',
    },
    version: 3,
    description: 'Automatic material consumption of a production entry (backflush — spec 0040 F4 / 0180; ' +
        'owner resolution — spec 0044 / request 0207)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'productionEntryId',
            label: 'Apontamento',
            type: 'string',
            required: true,
            description: 'Parent production entry id (also encoded in the path)',
        },
        {
            name: 'productionOrderMaterialId',
            label: 'Insumo da OP',
            type: 'string',
            required: false,
            description: 'Plain pointer to ProductionOrderMaterial (subcollection — no FK; resolved with OP context)',
        },
        {
            name: 'componentId',
            label: 'Componente',
            type: 'string',
            required: false,
        },
        {
            name: 'productId',
            label: 'Insumo',
            type: 'string',
            required: true,
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: false,
        },
        {
            name: 'quantity',
            label: 'Quantidade consumida',
            type: 'number',
            display: 'quantity',
            required: true,
        },
        {
            name: 'lotId',
            label: 'Lote',
            type: 'string',
            required: false,
            description: 'Always null in F4 — reserved for the lot arc',
        },
        {
            name: 'ownerPartyId',
            label: 'Proprietário',
            type: 'string',
            required: false,
            description: 'FK → Party; null = estoque próprio. Resolvido por FIFO no consumo (0044 §3 / 0207) — ' +
                'inferência operacional, não verdade fiscal.',
        },
        {
            name: 'stockLocationId',
            label: 'Local de estoque',
            type: 'string',
            required: false,
        },
        {
            name: 'stockStatus',
            label: 'Estoque',
            type: 'enum',
            required: false,
            enumValues: [...ProductionEntryOutputStockStatusValues],
            enumLabels: PRODUCTION_ENTRY_OUTPUT_STOCK_STATUS_LABELS,
            defaultValue: 'not-applicable',
        },
        {
            name: 'stockMovementId',
            label: 'Movimento',
            type: 'string',
            required: false,
        },
        {
            name: 'stockError',
            label: 'Erro de estoque',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        {
            name: 'stockIdempotencyKey',
            label: 'Chave de idempotência',
            type: 'string',
            required: false,
            description: 'Chave usada na última tentativa de postExit desta linha — reprocesso reusa em vez de ' +
                'recalcular (0207, achado de review). Null = nunca tentou postar.',
        },
    ],
    foreignKeys: [
        {
            fieldName: 'productionEntryId',
            targetEntity: 'ProductionEntry',
            relationship: 'many-to-one',
            displayField: 'id',
            description: 'Apontamento pai',
        },
        {
            fieldName: 'componentId',
            targetEntity: 'Component',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'stockLocationId',
            targetEntity: 'StockLocation',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'ownerPartyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
        },
    ],
};
//# sourceMappingURL=production-entry-material.descriptor.js.map